import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import moment from "moment";
import "moment/locale/ar";

const getBackgroundColor = (temp) => {
  if (temp <= 0) return "linear-gradient(to top, #E0EAFC, #CFDEF3)";
  if (temp <= 5) return "linear-gradient(to top, #6DD5FA, #2980B9)";
  if (temp <= 10) return "linear-gradient(to top, #4A5467, #3C80A7)";
  if (temp <= 15) return "linear-gradient(to top, #373b44, #4286f4)";
  if (temp <= 20) return "linear-gradient(to top, #2193b0, #6dd5ed)";
  if (temp <= 25) return "linear-gradient(to top, #fceabb, #f8b500)";
  if (temp <= 30) return "linear-gradient(to top, #FF8C00, #F3B000)";
  if (temp <= 35) return "linear-gradient(to top, #D32F2F, #FF7043)";
  if (temp <= 40) return "linear-gradient(to top, #B22222, #FF4500)";
  if (temp <= 45) return "linear-gradient(to top, #B71C1C, #D32F2F)";
  return "linear-gradient(to top, #6b0f1a, #b91372)";
};
const toArabicNumerals = (num) => {
  const arabicNumerals = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
  return String(num).replace(
    /[0-9]/g,
    (digit) => arabicNumerals[parseInt(digit)]
  );
};
export const WeatherCard = () => {
  const { t, i18n } = useTranslation();
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [city, setCity] = useState("");

  useEffect(() => {
    const source = axios.CancelToken.source();

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        axios
          .get(
            `https://api.weatherapi.com/v1/current.json?key=7bd478a5343043f08a6124028251410&q=${latitude},${longitude}`,
            { cancelToken: source.token }
          )

          .then((response) => {
            setWeather(response.data);
            setLoading(false);
          })

          .catch((err) => {
            if (!axios.isCancel(err)) {
              setError("fetchError");
              setLoading(false);
            }
          });
      },
      (geoError) => {
        setError("permissionError");
        setLoading(false);
      }
    );

    return () => {
      source.cancel();
    };
  }, []);

  const handleSearch = useCallback(
    (event) => {
      if (event) event.preventDefault();

      // Don't search if the input is empty
      if (!city.trim()) return;

      setLoading(true);
      setError(null); // Clear any previous errors

      axios
        .get(
          `https://api.weatherapi.com/v1/current.json?key=7bd478a5343043f08a6124028251410&q=${city}`
        )
        .then((response) => {
          setWeather(response.data);
        })
        .catch((err) => {
          // Set an error if the city is not found
          setError("cityNotFound");
        })
        .finally(() => {
          setLoading(false);
          setCity("");
        });
    },
    [city]
  );

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(to top, #373b44, #4286f4)",
        }}
      >
        <CircularProgress sx={{ color: "white" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(to top, #373b44, #4286f4)",
        }}
      >
        <Alert severity="error" sx={{ width: "80%", maxWidth: "500px" }}>
          {t(error)}
        </Alert>
      </Box>
    );
  }

  if (!weather) {
    return null;
  }

  const backgroundStyle = getBackgroundColor(weather.current.temp_c);
  const formattedDate = moment(weather.location.localtime)
    .locale(i18n.language)
    .format("ddd / D / MMM / YYYY");

  const handleLanguageChange = () => {
    i18n.changeLanguage(i18n.language === "ar" ? "en" : "ar");
  };

  const isArabic = i18n.language === "ar";

  return (
    <Box
      dir={isArabic ? "rtl" : "ltr"}
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: backgroundStyle,
        transition: "background 0.5s ease",
      }}
    >
      <Container maxWidth="sm">
        <Stack spacing={4} alignItems="center">
          <Box component="form" onSubmit={handleSearch} sx={{ width: "100%" }}>
            <Stack
              dir="ltr"
              direction="row"
              spacing={1}
              alignItems="center"
              width="100%"
            >
              <TextField
                fullWidth
                label={t("searchLabel")}
                variant="outlined"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.5)" },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.8)",
                    },
                    "&.Mui-focused fieldset": { borderColor: "white" },
                    color: "white",
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255, 255, 255, 0.7)",
                  },
                  "& .MuiInputLabel-root.Mui-focused": { color: "white" },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                sx={{
                  height: "56px",
                  minWidth: "120px",
                  background: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(10px)",
                  borderRadius: 2,
                  border: "1px solid rgba(255, 255, 255, 0.18)",
                  color: "#fff",
                  "&:hover": { background: "rgba(255, 255, 255, 0.3)" },
                  /* ... your existing sx styles ... */
                }}
              >
                {t("searchButton")}
              </Button>
            </Stack>
          </Box>
          <Card
            sx={{
              width: "100%",
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              borderRadius: 4,
              boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
              border: "1px solid rgba(255, 255, 255, 0.18)",
              color: "#fff",
            }}
          >
            <CardContent>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-around"
                pb={1}
              >
                <Typography variant="h4" component="h2" fontWeight="500">
                  {weather.location.name}
                </Typography>
                <Typography
                  variant="h6"
                  component="p"
                  sx={{ textAlign: "right" }}
                >
                  {formattedDate}
                </Typography>
              </Stack>
              <Divider
                sx={{ my: 1, borderColor: "rgba(255, 255, 255, 0.5)" }}
              />
              <Stack
                direction="row"
                justifyContent="space-around"
                alignItems="center"
                pt={2}
              >
                <Stack spacing={0.5}>
                  <Typography
                    variant="h1"
                    component="div"
                    fontWeight={300}
                    sx={{ display: "flex", alignItems: "flex-start" }}
                  >
                    {isArabic
                      ? toArabicNumerals(Math.round(weather.current.temp_c))
                      : Math.round(weather.current.temp_c)}
                    <Typography variant="h3" component="span" mt={1}>
                      {isArabic ? "°س" : "°C"}
                    </Typography>
                  </Typography>
                  <Typography
                    variant="h6"
                    textTransform="capitalize"
                    style={{ textAlign: "left" }}
                  >
                    {t(weather.current.condition.text)}
                  </Typography>
                </Stack>
                <Box
                  component="img"
                  src={`https:${weather.current.condition.icon}`}
                  alt={t(weather.current.condition.text)}
                  sx={{
                    width: "150px",
                    height: "150px",
                    filter: "drop-shadow(0px 5px 10px rgba(0,0,0,0.2))",
                  }}
                />
              </Stack>
            </CardContent>
          </Card>

          <Button
            onClick={handleLanguageChange}
            sx={{
              minWidth: "120px",
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              borderRadius: 2,
              border: "1px solid rgba(255, 255, 255, 0.18)",
              color: "#fff",
              "&:hover": { background: "rgba(255, 255, 255, 0.3)" },
            }}
            variant="contained"
          >
            {t("languageButton")}
          </Button>
        </Stack>
      </Container>
    </Box>
  );
};
